import React, { useId, useMemo, useState, useEffect } from 'react';
import { Box, Typography, Paper, Collapse, IconButton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const GROUP_COLORS = {
  bottom_chord: '#1565c0',
  top_chord: '#c62828',
  top_chord_left: '#c62828',
  top_chord_right: '#ad1457',
  diagonal: '#2e7d32',
  vertical: '#6a1b9a',
  column: '#5d4037',
  arch_rib: '#b71c1c',
  arch_leg: '#5d4037',
  column_outer: '#5d4037',
  column_inner: '#455a64',
  rigid_link: '#263238',
};

const LOAD_TOP = GROUP_COLORS.top_chord;
const LOAD_BOTTOM = GROUP_COLORS.bottom_chord;

function boundsFromGeometry(geom) {
  if (!geom?.nodes?.length) return { minX: 0, maxX: 10, minY: -2, maxY: 3, w: 10, h: 5 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  geom.nodes.forEach((n) => {
    minX = Math.min(minX, n.x);
    maxX = Math.max(maxX, n.x);
    minY = Math.min(minY, n.y);
    maxY = Math.max(maxY, n.y);
  });
  const pad = Math.max(maxX - minX, maxY - minY) * 0.12 || 0.5;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minY: minY - pad,
    maxY: maxY + pad,
    w: maxX - minX + 2 * pad,
    h: maxY - minY + 2 * pad,
  };
}

/** Outward normal with positive world-y (roof side / above chord) for a left→right segment */
function outwardNormalUp(dx, dy) {
  const L = Math.hypot(dx, dy) || 1;
  let nx = -dy / L;
  let ny = dx / L;
  if (ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { nx, ny };
}

function geometryHasPortalColumns(geom) {
  if (!geom?.members?.length) return false;
  return geom.members.some((m) => m.group === 'column');
}

/** Arc-length cumulative distances for ordered chord nodes */
function chordPolylineMetrics(pts) {
  if (!pts || pts.length < 2) return { total: 0, cum: [0], pts: [] };
  const cum = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) {
    total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    cum.push(total);
  }
  return { total, cum, pts };
}

/** Position and outward normal at arc length s along the chord polyline */
function interpChord(s, cum, pts) {
  const p = pts;
  if (p.length < 2) return null;
  const smax = cum[cum.length - 1];
  let sClamped = Math.min(Math.max(s, 0), smax > 0 ? smax : 0);
  if (smax <= 0) {
    const dx = p[1].x - p[0].x;
    const dy = p[1].y - p[0].y;
    const { nx, ny } = outwardNormalUp(dx, dy);
    return { x: p[0].x, y: p[0].y, nx, ny };
  }
  if (sClamped <= 0) {
    const dx = p[1].x - p[0].x;
    const dy = p[1].y - p[0].y;
    const { nx, ny } = outwardNormalUp(dx, dy);
    return { x: p[0].x, y: p[0].y, nx, ny };
  }
  if (sClamped >= smax) {
    const n = p.length;
    const dx = p[n - 1].x - p[n - 2].x;
    const dy = p[n - 1].y - p[n - 2].y;
    const { nx, ny } = outwardNormalUp(dx, dy);
    return { x: p[n - 1].x, y: p[n - 1].y, nx, ny };
  }
  let k = 1;
  while (k < cum.length && cum[k] < sClamped) k += 1;
  const s0 = cum[k - 1];
  const s1 = cum[k];
  const t = s1 > s0 ? (sClamped - s0) / (s1 - s0) : 0;
  const x = p[k - 1].x + t * (p[k].x - p[k - 1].x);
  const y = p[k - 1].y + t * (p[k].y - p[k - 1].y);
  const dx = p[k].x - p[k - 1].x;
  const dy = p[k].y - p[k - 1].y;
  const { nx, ny } = outwardNormalUp(dx, dy);
  return { x, y, nx, ny };
}

/**
 * Continuous UDL diagram: dashed load line, hatch ticks, arrows at uniform spacing along arc length.
 */
function continuousChordLoadGraphics({
  pts,
  color,
  tx,
  ty,
  ref,
  arrowLenWorld,
  tipGapWorld,
  markerId,
  keyPrefix,
}) {
  const { total, cum, pts: p } = chordPolylineMetrics(pts);
  if (total <= 0 || p.length < 2) return [];

  const out = [];
  const bandOuter = tipGapWorld + arrowLenWorld * 0.42;
  const nSample = Math.max(24, Math.ceil(total / Math.max(ref * 0.07, 1e-6)));
  let dPath = '';
  for (let si = 0; si <= nSample; si += 1) {
    const s = (si / nSample) * total;
    const q = interpChord(s, cum, p);
    if (!q) break;
    const xs = tx(q.x + q.nx * bandOuter);
    const ys = ty(q.y + q.ny * bandOuter);
    dPath += si === 0 ? `M ${xs} ${ys}` : ` L ${xs} ${ys}`;
  }
  out.push(
    <path
      key={`${keyPrefix}-band`}
      d={dPath}
      fill="none"
      stroke={color}
      strokeWidth={0.9}
      strokeDasharray="5 4"
      opacity={0.55}
    />,
  );

  const hatchSpacing = Math.max(ref * 0.032, total / 72);
  for (let s = 0; s <= total; s += hatchSpacing) {
    const q = interpChord(s, cum, p);
    if (!q) continue;
    const h0 = tipGapWorld + arrowLenWorld * 0.38;
    const h1 = tipGapWorld * 0.88;
    out.push(
      <line
        key={`${keyPrefix}-h-${s.toFixed(4)}`}
        x1={tx(q.x + q.nx * h0)}
        y1={ty(q.y + q.ny * h0)}
        x2={tx(q.x + q.nx * h1)}
        y2={ty(q.y + q.ny * h1)}
        stroke={color}
        strokeWidth={0.7}
        opacity={0.4}
      />,
    );
  }

  const nArrows = Math.max(5, Math.min(34, Math.round((total / Math.max(ref, 1e-6)) * 13)));
  for (let i = 0; i < nArrows; i += 1) {
    const s = ((i + 0.5) / nArrows) * total;
    const q = interpChord(s, cum, p);
    if (!q) continue;
    const tipXw = q.x + q.nx * tipGapWorld;
    const tipYw = q.y + q.ny * tipGapWorld;
    const startXw = tipXw + q.nx * arrowLenWorld;
    const startYw = tipYw + q.ny * arrowLenWorld;
    out.push(
      <line
        key={`${keyPrefix}-a-${i}`}
        x1={tx(startXw)}
        y1={ty(startYw)}
        x2={tx(tipXw)}
        y2={ty(tipYw)}
        stroke={color}
        strokeWidth={1.25}
        markerEnd={color === LOAD_TOP ? `url(#arrowTrussTop-${markerId})` : `url(#arrowTrussBot-${markerId})`}
      />,
    );
  }

  return out;
}

/**
 * Characteristic UDL display: positive load = downward (gravity).
 * Continuous load strip (dashed line + hatch) with arrows spaced by arc length along each chord.
 */
export default function TrussVisualization({
  geometry,
  lineLoads,
  title = 'Structure & loads',
  collapsible = false,
  defaultExpanded = true,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (collapsible) setExpanded(defaultExpanded);
  }, [collapsible, defaultExpanded]);
  const markerId = useId().replace(/:/g, '');
  const svgContent = useMemo(() => {
    if (!geometry?.nodes?.length) return null;
    const { minX, maxX, minY, maxY, w, h } = boundsFromGeometry(geometry);
    const hasPortal = geometryHasPortalColumns(geometry);
    const hasArch = (geometry.members || []).some((m) => m.group === 'arch_rib' || m.group === 'arch_leg');
    const W = 960;
    const padX = 12;
    const padTop = 28;
    const padBot = 22;
    let s = (W - 2 * padX) / Math.max(w, 1e-6);
    let H = Math.ceil(padTop + padBot + h * s);
    const Hmax = hasPortal || hasArch ? 560 : 300;
    if (H > Hmax) {
      s = (Hmax - padTop - padBot) / Math.max(h, 1e-6);
      H = Hmax;
    }
    const contentW = (maxX - minX) * s;
    const ox = (W - contentW) / 2;
    const tx = (x) => ox + (x - minX) * s;
    const ty = (y) => padTop + (maxY - y) * s;

    const wTop = Math.max(0, Number(lineLoads?.w_top_kN_m) || 0);
    const wBot = Math.max(0, Number(lineLoads?.w_bottom_kN_m) || 0);
    const ref = Math.max(maxX - minX, maxY - minY, 1);
    const tipGapWorld = ref * 0.009;
    const arrowLenWorld = ref * 0.026;

    const topNodes = (geometry.nodes || [])
      .filter((n) => n.role === 'top_chord' || n.role === 'top_chord_left' || n.role === 'top_chord_right')
      .sort((a, b) => a.x - b.x)
      .map((n) => ({ x: n.x, y: n.y }));
    const botNodes = (geometry.nodes || [])
      .filter((n) => n.role === 'bottom_chord')
      .sort((a, b) => a.x - b.x)
      .map((n) => ({ x: n.x, y: n.y }));

    const loadGraphics = [];
    if (wTop > 0 && topNodes.length > 1) {
      loadGraphics.push(
        ...continuousChordLoadGraphics({
          pts: topNodes,
          color: LOAD_TOP,
          tx,
          ty,
          ref,
          arrowLenWorld,
          tipGapWorld,
          markerId,
          keyPrefix: 'wt',
        }),
      );
    }
    if (wBot > 0 && botNodes.length > 1) {
      loadGraphics.push(
        ...continuousChordLoadGraphics({
          pts: botNodes,
          color: LOAD_BOTTOM,
          tx,
          ty,
          ref,
          arrowLenWorld,
          tipGapWorld,
          markerId,
          keyPrefix: 'wb',
        }),
      );
    }

    const lines = (geometry.members || []).map((m, idx) => {
      const a = geometry.nodes[m.i];
      const b = geometry.nodes[m.j];
      if (!a || !b) return null;
      const col = GROUP_COLORS[m.group] || '#666';
      return (
        <line
          key={m.key || idx}
          x1={tx(a.x)}
          y1={ty(a.y)}
          x2={tx(b.x)}
          y2={ty(b.y)}
          stroke={col}
          strokeWidth={m.group === 'rigid_link' ? 4 : m.group === 'column' ? 3.25 : 2.5}
          strokeLinecap="round"
        />
      );
    });

    const nodes = geometry.nodes.map((n) => (
      <circle
        key={n.id}
        cx={tx(n.x)}
        cy={ty(n.y)}
        r={n.role === 'column_base' ? 3.5 : 4}
        fill={n.role === 'column_base' ? '#5d4037' : '#333'}
      />
    ));

    const supports = (geometry.supports || []).map((su, i) => {
      const n = geometry.nodes[su.node];
      if (!n) return null;
      const x = tx(n.x);
      const y = ty(n.y);
      const b = 6;
      const tip = -4;
      if (su.type === 'roller_y' || (!su.ux && su.uy)) {
        return (
          <g key={`s${i}`}>
            <polygon points={`${x - 10},${y + b} ${x + 10},${y + b} ${x},${y + tip}`} fill="#444" />
            <line x1={x - 12} y1={y + b + 2} x2={x + 12} y2={y + b + 2} stroke="#444" strokeWidth={2} />
            <circle cx={x - 8} cy={y + b + 5} r={3} fill="none" stroke="#444" />
            <circle cx={x + 8} cy={y + b + 5} r={3} fill="none" stroke="#444" />
          </g>
        );
      }
      return (
        <g key={`s${i}`}>
          <polygon points={`${x - 10},${y + b} ${x + 10},${y + b} ${x},${y + tip}`} fill="#444" />
        </g>
      );
    });

    let archDim = null;
    if (hasArch) {
      const ribNodes = (geometry.nodes || []).filter((n) => n.role === 'arch_rib' || n.role === 'top_chord');
      if (ribNodes.length >= 2) {
        const xs = ribNodes.map((n) => Number(n.x));
        const ys = ribNodes.map((n) => Number(n.y));
        const spanM = Math.max(...xs) - Math.min(...xs);
        const ySpring = Math.min(ys[0], ys[ys.length - 1]);
        const riseM = Math.max(...ys) - ySpring;
        archDim = (
          <text x={12} y={H - 8} fontSize={11} fill="#555">
            {`Span ${spanM.toFixed(2)} m · rise ${riseM.toFixed(2)} m`}
          </text>
        );
      }
    }

    let portalHeightDim = null;
    if (hasPortal) {
      const bases = (geometry.nodes || [])
        .filter((n) => n.role === 'column_base')
        .sort((a, b) => a.x - b.x);
      const leftBase = bases[0];
      if (leftBase) {
        const topCand = (geometry.nodes || [])
          .filter((n) => n.role !== 'column_base')
          .filter((n) => Math.abs(Number(n.x) - Number(leftBase.x)) < 1e-6)
          .sort((a, b) => b.y - a.y)[0];
        if (topCand) {
          const xDim = tx(leftBase.x) - 24;
          const y1 = ty(leftBase.y);
          const y2 = ty(topCand.y);
          portalHeightDim = (
            <g>
              <line x1={xDim} y1={y1} x2={xDim} y2={y2} stroke="#777" strokeWidth={1} />
              <line x1={xDim - 7} y1={y1} x2={xDim + 7} y2={y1} stroke="#777" strokeWidth={1} />
              <line x1={xDim - 7} y1={y2} x2={xDim + 7} y2={y2} stroke="#777" strokeWidth={1} />
              <text x={xDim - 9} y={(y1 + y2) / 2} fontSize={10} fill="#555" textAnchor="end" dominantBaseline="middle">
                H
              </text>
            </g>
          );
        }
      }
    }

    const legendLoads =
      wTop > 0 || wBot > 0 ? (
        <g>
          <text x={12} y={16} fontSize={10} fill="#555">
            {wTop > 0 ? `Top UDL (↓ char.): ${wTop.toFixed(2)} kN/m  ` : ''}
            {wBot > 0 ? `Bottom UDL (↓ char.): ${wBot.toFixed(2)} kN/m` : ''}
          </text>
        </g>
      ) : null;

    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', height: 'auto' }}>
        <defs>
          <marker id={`arrowTrussTop-${markerId}`} markerWidth={5} markerHeight={5} refX={4} refY={2.5} orient="auto">
            <path d="M0,0 L5,2.5 L0,5 z" fill={LOAD_TOP} />
          </marker>
          <marker id={`arrowTrussBot-${markerId}`} markerWidth={5} markerHeight={5} refX={4} refY={2.5} orient="auto">
            <path d="M0,0 L5,2.5 L0,5 z" fill={LOAD_BOTTOM} />
          </marker>
        </defs>
        {lines}
        {loadGraphics}
        {nodes}
        {supports}
        {portalHeightDim}
        {archDim}
        {legendLoads}
      </svg>
    );
  }, [geometry, lineLoads, markerId]);

  const body = (
    <>
      {!geometry?.nodes?.length ? (
        <Typography color="text.secondary">Adjust inputs to show geometry.</Typography>
      ) : (
        <Box sx={{ width: '100%' }}>{svgContent}</Box>
      )}
    </>
  );

  return (
    <Paper sx={{ p: collapsible && !expanded ? 0.5 : 2, px: collapsible && !expanded ? 1 : 2, mb: 0 }}>
      {collapsible ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: expanded ? 1 : 0,
            minHeight: expanded ? undefined : 28,
          }}
        >
          <Typography variant={expanded ? 'subtitle1' : 'caption'} sx={{ fontWeight: expanded ? 600 : 500 }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse governing frame' : 'Expand governing frame'}
            onClick={() => setExpanded((open) => !open)}
            sx={{ p: 0.25 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      ) : (
        <Typography variant="subtitle1" gutterBottom>
          {title}
        </Typography>
      )}
      {collapsible ? <Collapse in={expanded}>{body}</Collapse> : body}
    </Paper>
  );
}
